/**
 * components/account/account-sections.ts
 *
 * The account settings page's section table — one entry per section, so a
 * section added later (a profile, notifications, appearance, API usage…) is
 * one entry here, its words under `account.sections` in both languages, and
 * its component in `AccountSettings`. The navigator, the address
 * (`/account/<slug>`), the heading over the section and who may open it all
 * follow from the entry.
 *
 * The page used to be billing alone, opened from the rail's account door. It
 * is the account's now (Oct 2026): payments sit beside the security tokens a
 * person hands their AI agents and scripts, and beside the switch that lets
 * those agents connect at all. Those two are the person's own rather than the
 * open property's, which is why the page stands open to somebody whose open
 * property is a read-only grant — only the payments section is not.
 *
 * The tokens and the AI agents are behind a switch an administrator turns on
 * for one person, on the Users console (Oct 2026): off until then, so a
 * member is offered the payments alone, and a viewer or staff no section at
 * all — the shell refuses them the page, and their account door opens
 * nothing. The server refuses the two sections' routes on the same grant.
 *
 * Adding a section: an entry here (key, slug, a glyph nothing else wears,
 * `followsOpenProperty`, `availableTo`, and `feature` where it stands behind
 * an administrator's switch), its words under `account.sections` in both
 * languages, and its component in `AccountSettings`.
 *
 * Kept out of the component so the route table and the shell's gates read it
 * without mounting the page — the editor's sections are kept apart for the
 * same reason (`settings/editor-sections.ts`).
 */

import { Bot, CreditCard, KeySquare, type LucideIcon } from "lucide-react";
import type { UserFeature } from "../../services/userFeatures";
import type { ReadOnlyReason } from "../access/read-only-access";

/** Every section's stable key, in the order the navigator lists them. */
export const ACCOUNT_SECTION_KEYS = ["billing", "tokens", "mcp"] as const;

export type AccountSectionKey = (typeof ACCOUNT_SECTION_KEYS)[number];

/**
 * What decides which sections a person may open: why the open property is
 * read-only, if it is, what an administrator switched on for them, and
 * whether their access to the open property reaches its whole account.
 */
export interface AccountSectionAccess {
    readOnlyReason: ReadOnlyReason | null;
    features: readonly UserFeature[];
    /**
     * Whether the caller's access to the open property reaches its whole
     * account (`/api/me`'s `activeConnectionWholeAccount`): an owner, or a
     * member whose access covers every property. False for a member given
     * some of the account's properties, who is not part of its
     * administration. Absent — a server from before scoped access — reads as
     * true, as every membership then was.
     */
    wholeAccount?: boolean;
}

export interface AccountSectionDefinition {
    /** Stable key — the i18n entry under `account.sections` and the route's `section`. */
    key: AccountSectionKey;
    /**
     * The section's address, `/account/<slug>`: what a bookmark, an e-mail
     * and a copied link carry, so it stays put when a title is reworded.
     */
    slug: string;
    /**
     * The glyph its navigator row wears — no two sections alike, and none the
     * rail already wears for something else (its footer's KeyRound is "reset
     * password", so the security tokens wear KeySquare).
     */
    icon: LucideIcon;
    /**
     * Whether it shows something of the open property. Payments do — billing
     * belongs to the account the open property sits in — so a property opened
     * in another tab hides them behind the shell's notice, as it hides every
     * property's page. Tokens and the AI-agent switch are the person's own,
     * whatever property is open, and stay on screen.
     */
    followsOpenProperty: boolean;
    /** Whether somebody may open it, given why the open property is read-only. */
    availableTo(access: AccountSectionAccess): boolean;
    /**
     * The feature an administrator switches on before the section is offered,
     * whatever `availableTo` says; absent where nothing has to be. Off is the
     * default, so a section behind one is nobody's until an administrator acts.
     */
    feature?: UserFeature;
}

/**
 * Payments are for the people who run the open property's account. Billing
 * belongs to the account a property sits in, so a read-only viewer — a guest
 * on somebody else's account — an administrator looking in read-only, and a
 * member given only some of the account's properties pay for nothing here:
 * the server resolves their billing to an account of their own either way,
 * and offering the section would show them a page about another one.
 */
function runsTheOpenProperty({ readOnlyReason, wholeAccount }: AccountSectionAccess): boolean {
    return readOnlyReason === null && wholeAccount !== false;
}

/**
 * Tokens and the AI-agent switch belong to the person, never to the open
 * property: a token reaches only the properties its holder runs, decided by
 * the server on every call, so whatever property happens to be open has no
 * say in whether they may be managed.
 */
function everybodySignedIn(): boolean {
    return true;
}

/**
 * What the page hands every section's component: a way to open another
 * section — the tokens' "Connect an AI agent", the AI agents' "Create a
 * security token" — through the same move the navigator makes, so the
 * address follows and Back returns.
 */
export interface AccountSectionProps {
    onSelectSection: (key: AccountSectionKey) => void;
    /**
     * The server refused the section for a feature an administrator switched
     * off while it was open (`FEATURE_NOT_ENABLED`): the shell reads the
     * person again, so the navigator and the rail follow. Optional, as a
     * section drawn on its own — in its tests — has no shell to ask.
     */
    onFeatureWithdrawn?: () => void;
}

export const ACCOUNT_SECTIONS: readonly AccountSectionDefinition[] = [
    { key: "billing", slug: "billing", icon: CreditCard, followsOpenProperty: true, availableTo: runsTheOpenProperty },
    // The tokens exist only for the AI agents, so the two share one switch.
    { key: "tokens", slug: "tokens", icon: KeySquare, followsOpenProperty: false, availableTo: everybodySignedIn, feature: "mcp" },
    // Named for what the person connects, not for the protocol: "ai-agents"
    // reads in an address bar as the screen it opens.
    { key: "mcp", slug: "ai-agents", icon: Bot, followsOpenProperty: false, availableTo: everybodySignedIn, feature: "mcp" },
];

export const ACCOUNT_SECTION_BY_KEY = Object.fromEntries(
    ACCOUNT_SECTIONS.map((section) => [section.key, section]),
) as Record<AccountSectionKey, AccountSectionDefinition>;

const ACCOUNT_SECTION_BY_SLUG = new Map(ACCOUNT_SECTIONS.map((section) => [section.slug, section.key]));

/**
 * Whether the section on screen shows something of the open property, so the
 * shell hides it while another property is open (`useOpenPropertyWatch`). An
 * address naming no section is judged by the one the page is about to open for
 * this reader ({@link defaultAccountSection}): payments for a member, so
 * nothing of a property opened elsewhere is drawn; the tokens for a viewer or
 * staff, whose door to their own tokens must not be hidden behind the notice
 * for a section they are never offered.
 *
 * With no section to open — a viewer or staff without the AI agents — the
 * shell refuses the page, and were it drawn it would draw no section: nothing
 * of a property, so false. True would put the notice over a page that holds
 * nothing to hide.
 */
export function accountSectionFollowsOpenProperty(
    key: AccountSectionKey | undefined,
    access: AccountSectionAccess,
): boolean {
    const shown = key ?? defaultAccountSection(access);
    return shown === null ? false : ACCOUNT_SECTION_BY_KEY[shown].followsOpenProperty;
}

/** The section an address names, or null where none answers to it. */
export function accountSectionFromSlug(slug: string): AccountSectionKey | null {
    return ACCOUNT_SECTION_BY_SLUG.get(slug) ?? null;
}

/** Who may open a section: its own rule, and the administrator's switch where it stands behind one. */
function isOffered(section: AccountSectionDefinition, access: AccountSectionAccess): boolean {
    if (section.feature !== undefined && !access.features.includes(section.feature)) return false;
    return section.availableTo(access);
}

export function isAccountSectionAvailable(key: AccountSectionKey, access: AccountSectionAccess): boolean {
    return isOffered(ACCOUNT_SECTION_BY_KEY[key], access);
}

/** The sections somebody may open, in the navigator's order. */
export function availableAccountSections(access: AccountSectionAccess): AccountSectionKey[] {
    return ACCOUNT_SECTIONS.filter((section) => isOffered(section, access)).map((section) => section.key);
}

/**
 * The section the page opens on when the address names none, or one the
 * person may not open: the first they may. Null where there is none — a
 * viewer or staff whom no administrator gave the AI agents — and then the
 * page is not theirs to open at all (`useDashboardInit`).
 */
export function defaultAccountSection(access: AccountSectionAccess): AccountSectionKey | null {
    return availableAccountSections(access)[0] ?? null;
}
